import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { AppTopRightMenu } from "@/components/app-layout/AppTopRightMenu";
import { DeleteDialog } from "@/components/dialogs/DeleteDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TableSkeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCustomPromptsData } from "@/data/use-custom-prompts-data";
import { useGroupData } from "@/data/use-groups-data";
import { usePromptBaseNamesData } from "@/data/use-prompt-base-names-data";
import type { CustomPromptDto } from "@/lib/gen-api/types.gen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faPen,
  faPlus,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";

const SNAKE_CASE = new RegExp(/^[a-z0-9_]+$/);

// ============================================================
// Create / Edit Dialog
// ============================================================

const CustomPromptDialog = ({
  open,
  onOpenChange,
  availableBases,
  initial,
  isSubmitting,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableBases: readonly string[];
  initial?: CustomPromptDto | null;
  isSubmitting: boolean;
  onSubmit: (params: { base: string; name: string }) => Promise<void>;
}) => {
  const isEditing = !!initial;
  const [base, setBase] = useState(
    initial?.base ? initial.base : (availableBases[0] ?? "chat"),
  );
  const [name, setName] = useState(initial?.name ?? "");

  const isNameValid = useMemo(() => {
    return SNAKE_CASE.test(name) && name.endsWith(`_${base}`);
  }, [name, base]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Custom Prompt" : "Add Custom Prompt"}
          </DialogTitle>
          <DialogDescription>
            Map a group override to a custom prompt name. Custom prompt content
            is managed in Langfuse.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium" htmlFor="base-prompt">
              Base prompt
            </label>
            {isEditing ? (
              <Input id="base-prompt" value={base} disabled />
            ) : (
              <Select value={base} onValueChange={setBase}>
                <SelectTrigger id="base-prompt">
                  <SelectValue placeholder="Select base prompt" />
                </SelectTrigger>
                <SelectContent>
                  {availableBases.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium" htmlFor="prompt-name">
              Custom Prompt Name
            </label>
            {!isNameValid && (
              <div className="text-destructive text-xs">
                Must be in snake_case and end with "_{base}"
              </div>
            )}
            <Input
              id="prompt-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={`e.g. acme_${base}`}
              onKeyDown={(event) => {
                if (event.key === "Enter" && isNameValid && !isSubmitting) {
                  void onSubmit({ base, name: name.trim() });
                }
              }}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={!isNameValid || isSubmitting}
            onClick={() => void onSubmit({ base, name: name.trim() })}
          >
            {isEditing ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// Page
// ============================================================

export const GlobalAdminGroupCustomPromptsPage = () => {
  const { groupUuid } = useParams({ strict: false }) as {
    groupUuid?: string;
  };
  const { group } = useGroupData(groupUuid);
  const {
    customPrompts,
    isLoading,
    createCustomPrompt,
    isCreating,
    updateCustomPrompt,
    isUpdating,
    deleteCustomPrompt,
    isDeleting,
  } = useCustomPromptsData(groupUuid);
  const { baseNames } = usePromptBaseNamesData();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<CustomPromptDto | null>(
    null,
  );
  const [deletingPrompt, setDeletingPrompt] = useState<CustomPromptDto | null>(
    null,
  );

  const availableBases = useMemo(() => {
    const used = new Set(customPrompts.map((prompt) => prompt.base));
    return baseNames.filter((base) => !used.has(base));
  }, [customPrompts, baseNames]);

  const handleCreate = async (params: { base: string; name: string }) => {
    try {
      await createCustomPrompt(params);
      toast.success("Custom prompt created.");
      setIsCreateOpen(false);
    } catch {
      toast.error("Failed to create custom prompt.");
    }
  };

  const handleUpdate = async (params: { base: string; name: string }) => {
    if (!editingPrompt) return;
    try {
      await updateCustomPrompt({
        customPromptUuid: editingPrompt.uuid,
        body: { name: params.name },
      });
      toast.success("Custom prompt updated.");
      setEditingPrompt(null);
    } catch {
      toast.error("Failed to update custom prompt.");
    }
  };

  const handleDelete = async () => {
    if (!deletingPrompt) return;
    try {
      await deleteCustomPrompt(deletingPrompt.uuid);
      toast.success("Custom prompt deleted.");
      setDeletingPrompt(null);
    } catch {
      toast.error("Failed to delete custom prompt.");
    }
  };

  if (!group) return null;

  return (
    <>
      <AppTopRightMenu.ContentPortal>
        <Button
          disabled={availableBases.length === 0}
          onClick={() => setIsCreateOpen(true)}
        >
          <FontAwesomeIcon icon={faPlus} />
          Add
        </Button>
      </AppTopRightMenu.ContentPortal>
      <Page>
        <PageHeader>
          <PageHeader.Title>Custom Prompts</PageHeader.Title>
        </PageHeader>
        <PageBody innerClassName="gap-6">
          <p className="text-muted-foreground text-sm">
            Override default prompts for this group by pointing to a custom
            prompt name. Delete an override to fall back to the global default.
          </p>

          {isLoading && <TableSkeleton />}

          {!isLoading && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Base</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead></TableHead>
                  <TableHead>Custom</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customPrompts.map((prompt) => (
                  <TableRow key={prompt.uuid}>
                    <TableCell className="text-muted-foreground font-medium">
                      {prompt.base}
                    </TableCell>
                    <TableCell className="text-muted-foreground font-medium">
                      default_{prompt.base}
                    </TableCell>
                    <TableCell>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </TableCell>
                    <TableCell className="font-medium">{prompt.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setEditingPrompt(prompt)}
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setDeletingPrompt(prompt)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!isLoading && customPrompts.length === 0 && (
            <div className="text-muted-foreground py-4 text-center">
              No custom prompts yet. This group uses the global defaults.
            </div>
          )}
        </PageBody>

        {isCreateOpen && (
          <CustomPromptDialog
            key="create"
            open={isCreateOpen}
            onOpenChange={setIsCreateOpen}
            availableBases={availableBases}
            isSubmitting={isCreating}
            onSubmit={handleCreate}
          />
        )}

        {editingPrompt && (
          <CustomPromptDialog
            key={editingPrompt.uuid}
            open={!!editingPrompt}
            onOpenChange={(open) => {
              if (!open) setEditingPrompt(null);
            }}
            availableBases={[editingPrompt.base]}
            initial={editingPrompt}
            isSubmitting={isUpdating}
            onSubmit={handleUpdate}
          />
        )}

        <DeleteDialog
          name={
            deletingPrompt ? `${deletingPrompt.name} (custom)` : "custom prompt"
          }
          open={!!deletingPrompt}
          disabled={isDeleting}
          onOpenChange={(open) => {
            if (!open) setDeletingPrompt(null);
          }}
          onDeleteClick={() => {
            void handleDelete();
          }}
        />
      </Page>
    </>
  );
};
